
import { useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AuthStateType } from "./useAuthState";
import { generateSecureId } from "@/utils/security";
import { useActivityTracking } from "./useActivityTracking";

/**
 * Custom hook to handle authentication events
 */
export const useAuthEventHandlers = (
  authState: AuthStateType,
  signOut: () => Promise<void>
) => {
  const {
    setSession,
    setUser,
    setIsLoading,
    isMounted,
    authTimeoutRef,
  } = authState;

  const { recordActivity } = useActivityTracking(authState, signOut);

  // Set up authentication event listeners
  useEffect(() => {
    // Set a timeout to prevent hanging in loading state
    authTimeoutRef.current = setTimeout(() => {
      if (isMounted.current && authState.isLoading) {
        console.warn("Auth provider timed out waiting for session");
        setIsLoading(false);
      }
    }, 5000);
    
    // Set up the auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event);
        if (isMounted.current) {
          setSession(session);
          setUser(session?.user ?? null);
          
          // Only finalize loading for non-initial events to avoid race with getSession()
          if (event !== 'INITIAL_SESSION') {
            setIsLoading(false);
            
            // Clear the timeout as we got a response
            if (authTimeoutRef.current) {
              clearTimeout(authTimeoutRef.current);
              authTimeoutRef.current = null;
            }
          }
          
          // Record activity on auth state change
          recordActivity();
          
          // Generate a unique session identifier for checkout operations
          if (event === 'SIGNED_IN') {
            localStorage.setItem('sessionId', generateSecureId());
            toast.success("Signed in successfully");
          } else if (event === 'SIGNED_OUT') {
            localStorage.removeItem('sessionId');
            toast.success("Signed out successfully");
          } else if (event === 'TOKEN_REFRESHED') {
            console.log("Auth token refreshed");
          } else if (event === 'USER_UPDATED') {
            toast.success("User profile updated");
          }
        }
      }
    );

    // Then check for the initial session
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (isMounted.current) {
          setSession(session);
          setUser(session?.user ?? null);
          setIsLoading(false);
          
          // Record initial activity
          recordActivity();
          
          // Clear the timeout as we got a response
          if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
            authTimeoutRef.current = null;
          }
        }
      } catch (error) {
        console.error("Error getting initial session:", error);
        if (isMounted.current) {
          setIsLoading(false);
          
          // Clear the timeout as we got a response (error)
          if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
            authTimeoutRef.current = null;
          }
        }
      }
    };
    
    getInitialSession();

    // Clean up the listener when the component unmounts
    return () => {
      subscription.unsubscribe();
      
      // Clear the timeout if it exists
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
    };
  }, [authState, authTimeoutRef, isMounted, recordActivity, setIsLoading, setSession, setUser]);
};
